import { supabase } from '../lib/supabase';

export const pipelineService = {
  // Get pipeline stages for a project
  async getPipelineStages(projectId) {
    try {
      const { data, error } = await supabase
        ?.rpc('get_pipeline_stages', {
          project_uuid: projectId
        });

      if (error) throw error;

      return data?.map(stage => ({
        id: stage?.id,
        stageName: stage?.stage_name,
        stageOrder: stage?.stage_order,
        color: stage?.color,
        icon: stage?.icon,
        leadCount: parseInt(stage?.lead_count || 0),
        totalValue: parseFloat(stage?.total_value || 0)
      }));
    } catch (error) {
      console.error('Error fetching pipeline stages:', error);
      throw error;
    }
  },

  // Get all pipeline stages (raw)
  async getAllStages(projectId) {
    try {
      const { data, error } = await supabase
        ?.from('pipeline_stages')
        ?.select('*')
        ?.eq('project_id', projectId)
        ?.eq('is_active', true)
        ?.order('stage_order', { ascending: true });

      if (error) throw error;

      return data?.map(stage => ({
        id: stage?.id,
        projectId: stage?.project_id,
        stageName: stage?.stage_name,
        stageOrder: stage?.stage_order,
        color: stage?.color,
        icon: stage?.icon,
        isActive: stage?.is_active,
        isFinal: stage?.is_final,
        automationRules: stage?.automation_rules,
        createdAt: stage?.created_at,
        updatedAt: stage?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching all stages:', error);
      throw error;
    }
  },

  // Create pipeline stage
  async createStage(stageData) {
    try {
      const { data, error } = await supabase
        ?.from('pipeline_stages')
        ?.insert({
          project_id: stageData?.projectId,
          stage_name: stageData?.stageName,
          stage_order: stageData?.stageOrder,
          color: stageData?.color || '#3B82F6',
          icon: stageData?.icon || 'Circle',
          is_active: stageData?.isActive !== undefined ? stageData?.isActive : true,
          is_final: stageData?.isFinal || false,
          automation_rules: stageData?.automationRules || {}
        })
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        projectId: data?.project_id,
        stageName: data?.stage_name,
        stageOrder: data?.stage_order,
        color: data?.color,
        icon: data?.icon,
        isActive: data?.is_active,
        isFinal: data?.is_final,
        automationRules: data?.automation_rules,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error creating stage:', error);
      throw error;
    }
  },

  // Update pipeline stage
  async updateStage(stageId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('pipeline_stages')
        ?.update({
          stage_name: updates?.stageName,
          stage_order: updates?.stageOrder,
          color: updates?.color,
          icon: updates?.icon,
          is_active: updates?.isActive,
          is_final: updates?.isFinal,
          automation_rules: updates?.automationRules
        })
        ?.eq('id', stageId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        projectId: data?.project_id,
        stageName: data?.stage_name,
        stageOrder: data?.stage_order,
        color: data?.color,
        icon: data?.icon,
        isActive: data?.is_active,
        isFinal: data?.is_final,
        automationRules: data?.automation_rules,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating stage:', error);
      throw error;
    }
  },

  // Delete pipeline stage
  async deleteStage(stageId) {
    try {
      const { error } = await supabase
        ?.from('pipeline_stages')
        ?.delete()
        ?.eq('id', stageId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting stage:', error);
      throw error;
    }
  },

  // Reorder stages
  async reorderStages(projectId, stageOrders) {
    try {
      const updates = stageOrders?.map(({ id, order }) => 
        supabase
          ?.from('pipeline_stages')
          ?.update({ stage_order: order })
          ?.eq('id', id)
      );

      await Promise.all(updates);
    } catch (error) {
      console.error('Error reordering stages:', error);
      throw error;
    }
  }
};

export default pipelineService;